import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ContactButton from '../UI/ContactButton';
import LoyaltyAchievementModal from '../UI/LoyaltyAchievementModal';
import NotificationBell from '../UI/NotificationBell';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import { DashboardStatsSkeleton } from '../UI/Skeleton';
import { checkLoyaltyPopup } from '../../utils/supabaseAPI';
import {
  CreditCard, Clock, Plus, Star, Award, RefreshCw,
  PiggyBank, Home, GraduationCap, ChevronRight,
  AlertTriangle, CheckCircle, TrendingUp, Wallet, LogOut
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const NAVY = '#0f2d6b';
const GOLD = '#e8a020';

const firstName = (u) => u?.first_name || u?.user_metadata?.first_name || 'Bienvenue';

const greet = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bonjour';
  if (h < 18) return 'Bon après-midi';
  return 'Bonsoir';
};

const DueChip = ({ days }) => {
  if (days == null) return null;
  if (days < 0)  return <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[11px] font-bold">Retard {Math.abs(days)}j</span>;
  if (days === 0) return <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] font-bold">Aujourd'hui</span>;
  return <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold">Dans {days}j</span>;
};

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { stats, loading, error, refetch: loadStats, isFetching } = useDashboardStats(user?.id);
  const { logout } = useAuth();
  const [showLoyaltyModal, setShowLoyaltyModal] = useState(false);
  const [loyaltyModalData, setLoyaltyModalData] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    checkLoyaltyPopup(user.id)
      .then(r => { if (r.success && r.showPopup) { setLoyaltyModalData(r.notification); setShowLoyaltyModal(true); } })
      .catch(() => {});
  }, [user?.id]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
          <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-red-500" />
          </div>
          <h2 className="text-base font-bold text-slate-900 mb-2">Impossible de charger</h2>
          <p className="text-slate-400 text-sm mb-5">{error}</p>
          <button onClick={() => loadStats()} className="flex items-center justify-center gap-2 w-full px-4 py-3 text-white rounded-xl font-semibold" style={{ background: NAVY }}>
            <RefreshCw size={15} /> Réessayer
          </button>
        </div>
      </div>
    );
  }

  const services = [
    { id: 'ab-pret',    title: 'AB Prêt',    icon: CreditCard,    bg: '#EFF6FF', color: '#2563EB', path: '/loan-request',      badge: stats.activeLoans > 0 ? stats.activeLoans : null },
    { id: 'ab-epargne', title: 'AB Épargne', icon: PiggyBank,     bg: '#F0FDF4', color: '#16A34A', path: '/ab-epargne' },
    { id: 'ab-logement',title: 'Logement',   icon: Home,          bg: '#F5F3FF', color: '#7C3AED', path: '/ab-logement' },
    { id: 'coaching',   title: 'Coaching',   icon: GraduationCap, bg: '#FFF7ED', color: '#EA580C', path: '/coaching-finance' },
  ];

  const hasActiveLoan = stats.activeLoans > 0;
  const isOverdue     = stats.daysUntilNextPayment < 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">

      {/* ─── HERO (compact) ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1a3f8f 100%)` }}>
        {/* Decorative */}
        <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-6 -left-6 w-28 h-28 rounded-full pointer-events-none" style={{ background: `${GOLD}15` }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            {/* Left: logo + greeting */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <img src="/logo-campus-finance.png" alt="" className="w-6 h-6 object-contain"
                  onError={e => { e.target.style.display = 'none'; }} />
              </div>
              <div className="min-w-0">
                <p className="text-white/55 text-[11px] font-medium tracking-wide leading-none mb-0.5">AB Campus Finance</p>
                <h1 className="text-white font-bold text-base sm:text-lg leading-tight truncate">
                  {greet()}, <span style={{ color: GOLD }}>{firstName(user)}</span> 👋
                </h1>
                {/* Stars loyalty inline */}
                <div className="flex items-center gap-1 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={10}
                      className={i < (stats.loyaltyScore ?? 0) ? 'fill-current' : 'opacity-25'}
                      style={{ color: i < (stats.loyaltyScore ?? 0) ? GOLD : '#fff' }}
                    />
                  ))}
                  <span className="text-[10px] font-semibold ml-0.5" style={{ color: '#e8d49a' }}>
                    {stats.loyaltyScore ?? 0}/5
                  </span>
                </div>
              </div>
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {/* Notifications */}
              <NotificationBell className="sm:block" />

              {/* Refresh */}
              <button onClick={() => loadStats()} disabled={isFetching}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                title="Actualiser">
                <RefreshCw size={13} className={`text-white/70 ${isFetching ? 'animate-spin' : ''}`} />
              </button>

              {/* Avatar / Profil */}
              <button onClick={() => navigate('/profile')}
                className="flex items-center justify-center w-8 h-8 rounded-xl overflow-hidden flex-shrink-0 ring-2 ring-white/20 hover:ring-white/40 transition-all"
                title="Mon profil"
                style={{ background: GOLD }}>
                <span className="text-xs font-extrabold leading-none" style={{ color: NAVY }}>
                  {(firstName(user)[0] || '?').toUpperCase()}
                  {(user?.last_name?.[0] || user?.user_metadata?.last_name?.[0] || '').toUpperCase()}
                </span>
              </button>

              {/* Déconnexion */}
              <button
                onClick={async () => { await logout(); navigate('/login'); }}
                className="w-8 h-8 rounded-xl bg-white/10 hover:bg-red-500/40 flex items-center justify-center transition-colors"
                title="Se déconnecter">
                <LogOut size={13} className="text-white/70" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MAIN CONTENT ──────────────────────────────────────────────────────── */}
      {/* Mobile: single column | Desktop (lg): 2 columns */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 pb-4">

        {/* Fetching indicator */}
        {isFetching && !loading && (
          <div className="flex items-center justify-center gap-2 text-slate-400 text-xs mb-3">
            <div className="w-3 h-3 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" />
            Mise à jour…
          </div>
        )}

        <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-4 lg:space-y-0">

          {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Stats card */}
            {loading ? <DashboardStatsSkeleton /> : (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="grid grid-cols-2 divide-x divide-slate-100">
                  {/* Savings */}
                  <div className="px-4 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                        <PiggyBank size={14} style={{ color: '#16A34A' }} />
                      </div>
                      <span className="text-slate-400 text-xs">Épargne</span>
                    </div>
                    <p className="text-slate-900 text-lg font-extrabold leading-none">{formatCurrency(stats.savingsBalance)}</p>
                    <p className="text-slate-400 text-[11px] mt-1">Solde disponible</p>
                  </div>

                  {/* Next payment */}
                  <div className="px-4 py-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: isOverdue ? '#FEF2F2' : '#FFF7ED' }}>
                        <Clock size={14} style={{ color: isOverdue ? '#DC2626' : '#EA580C' }} />
                      </div>
                      <span className="text-slate-400 text-xs">Paiement</span>
                    </div>
                    <p className="text-lg font-extrabold leading-none"
                      style={{ color: isOverdue ? '#DC2626' : '#0f172a' }}>
                      {formatCurrency(stats.nextPayment)}
                    </p>
                    <div className="mt-1">
                      {hasActiveLoan
                        ? <DueChip days={stats.daysUntilNextPayment} />
                        : <p className="text-slate-400 text-[11px]">Aucun prêt actif</p>}
                    </div>
                  </div>
                </div>

                {/* Loyalty strip */}
                <div className="border-t border-slate-100 px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Award size={13} style={{ color: GOLD }} />
                    <span className="text-slate-500 text-xs">Fidélité</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="w-5 h-1.5 rounded-full"
                          style={{ background: i < (stats.loyaltyScore ?? 0) ? GOLD : '#E2E8F0' }} />
                      ))}
                    </div>
                    <span className="text-xs font-bold" style={{ color: GOLD }}>{stats.loyaltyScore ?? 0}/5</span>
                  </div>
                </div>
              </div>
            )}

            {/* Active loan card */}
            {!loading && hasActiveLoan && (
              <div className="rounded-2xl p-4 text-white relative overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1e4db7 100%)` }}>
                <div className="absolute -top-5 -right-5 w-24 h-24 rounded-full bg-white/5 pointer-events-none" />

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-white/15 flex items-center justify-center">
                        <CreditCard size={12} className="text-white" />
                      </div>
                      <span className="text-white/70 text-xs">Prêt en cours</span>
                    </div>
                    {isOverdue
                      ? <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/30 text-red-200 text-[11px] font-bold"><AlertTriangle size={9} /> Retard</span>
                      : <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-200 text-[11px] font-bold"><CheckCircle size={9} /> Actif</span>}
                  </div>

                  <p className="text-white text-xl font-extrabold leading-none mb-0.5">{formatCurrency(stats.nextPayment)}</p>
                  <p className="text-white/55 text-[11px] mb-3">
                    {isOverdue
                      ? `En retard de ${Math.abs(stats.daysUntilNextPayment)} jour${Math.abs(stats.daysUntilNextPayment) > 1 ? 's' : ''}`
                      : stats.daysUntilNextPayment === 0 ? "Échéance aujourd'hui"
                      : `Échéance dans ${stats.daysUntilNextPayment} jour${stats.daysUntilNextPayment > 1 ? 's' : ''}`}
                  </p>

                  <button onClick={() => navigate('/repayment')}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-bold text-sm active:scale-[0.98] transition-transform"
                    style={{ background: GOLD, color: NAVY }}>
                    <TrendingUp size={15} /> Rembourser
                  </button>
                </div>
              </div>
            )}

            {/* New loan CTA */}
            {!loading && !hasActiveLoan && (
              <button onClick={() => navigate('/loan-request')}
                className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${NAVY}10` }}>
                    <Plus size={18} style={{ color: NAVY }} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-900 text-sm">Nouvelle demande de prêt</p>
                    <p className="text-slate-400 text-xs">Réponse en 24h · 100% en ligne</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </button>
            )}

            {/* Savings invite */}
            {!loading && stats.savingsBalance === 0 && (
              <button onClick={() => navigate('/ab-epargne')}
                className="w-full flex items-center justify-between px-4 py-3.5 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-[0.99] transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#F0FDF4' }}>
                    <Wallet size={18} style={{ color: '#16A34A' }} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-slate-900 text-sm">Commencer à épargner</p>
                    <p className="text-slate-400 text-xs">Intérêts 5% / mois · Sécurisé</p>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-slate-500 transition-colors" />
              </button>
            )}
          </div>

          {/* ── RIGHT COLUMN (also visible mobile, stacked below) ──────────── */}
          <div className="space-y-4">

            {/* Services grid */}
            <div>
              <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-wider mb-3">Nos services</p>
              <div className="grid grid-cols-4 lg:grid-cols-2 gap-3">
                {services.map(s => (
                  <button key={s.id} onClick={() => navigate(s.path)}
                    className="relative flex flex-col lg:flex-row items-center lg:items-center gap-2 lg:gap-3 py-4 lg:py-3.5 px-2 lg:px-4 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md active:scale-95 lg:active:scale-[0.99] transition-all duration-150">
                    {s.badge && (
                      <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full text-white text-[10px] font-extrabold flex items-center justify-center shadow-sm"
                        style={{ background: NAVY }}>{s.badge}</span>
                    )}
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: s.bg }}>
                      <s.icon size={20} style={{ color: s.color }} />
                    </div>
                    <div className="lg:text-left text-center">
                      <p className="text-slate-800 text-[11px] lg:text-sm font-semibold leading-tight">{s.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Quick info card (desktop only filler) */}
            <div className="hidden lg:block bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Award size={16} style={{ color: GOLD }} />
                <span className="text-slate-700 text-sm font-semibold">Votre profil</span>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Score fidélité', value: `${stats.loyaltyScore ?? 0} / 5 ⭐`, color: GOLD },
                  { label: 'Prêts actifs', value: `${stats.activeLoans}`, color: '#2563EB' },
                  { label: 'Épargne totale', value: formatCurrency(stats.savingsBalance), color: '#16A34A' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                    <span className="text-slate-500 text-sm">{label}</span>
                    <span className="font-bold text-sm" style={{ color }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>{/* /right */}

        </div>{/* /grid */}
      </div>{/* /main content */}

      <ContactButton />

      <LoyaltyAchievementModal
        isOpen={showLoyaltyModal}
        onClose={() => setShowLoyaltyModal(false)}
        userName={loyaltyModalData?.userName || firstName(user)}
        onViewBenefits={() => navigate('/loyalty-score')}
        onContactAdmin={() => {}}
      />
    </div>
  );
};

export default ClientDashboard;
